import { useCallback, useEffect, useState } from 'react';

import type { AccountInfo } from '@azure/msal-browser';

import type { UserRole as UserRoleEnum } from '@backend/db/const/db.const.ENUMS';

import { getActiveAccount } from '@frontend/features/auth/util/util.auth.getActiveAccount';
import { getAuthMode } from '@frontend/features/auth/util/util.auth.getAuthMode';
import { getLocalToken } from '@frontend/features/auth/util/util.auth.localToken';

export type UserRole = UserRoleEnum | null;

interface CurrentUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
}

interface UseCurrentUserResult {
  user: CurrentUser | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to get the current authenticated user
 *
 * **E2E Mode** (token in localStorage):
 * - Parses user from localStorage token
 * - Extracts roles from token claims
 *
 * **MSAL Mode** (no token in localStorage):
 * - Gets user from MSAL active account
 * - Extracts roles from ID token claims
 *
 * @returns Current user information, loading state, and refetch function
 */
export function useCurrentUser(): UseCurrentUserResult {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getCurrentUser = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const authMode = getAuthMode();

      // E2E mode: parse user from localStorage token
      if (authMode === 'e2e') {
        const localToken = getLocalToken();
        if (!localToken) {
          throw new Error('E2E mode active but no token found');
        }
        // Parse JWT token to get claims
        try {
          const base64Url = localToken.split('.')[1];
          if (!base64Url) {
            throw new Error('Invalid token format');
          }
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join(''),
          );
          const claims = JSON.parse(jsonPayload);

          // Extract roles from token claims
          const roles = (claims.roles as string[]) || [];

          // Determine user role
          let userRole: UserRole;
          if (roles.length === 0) {
            userRole = null;
          } else if (roles.includes('Admin')) {
            userRole = 'Admin';
          } else if (roles.includes('PNOps')) {
            userRole = 'PNOps';
          } else if (roles.includes('ViewOnly')) {
            userRole = 'ViewOnly';
          } else {
            userRole = 'ViewOnly';
          }

          // Create user from E2E token claims
          const currentUser: CurrentUser = {
            id: claims.oid || claims.sub || claims.appid || 'e2e-user',
            email: claims.email || claims.preferred_username || 'e2e@test.com',
            name: claims.name || 'E2E Test User',
            role: userRole,
          };

          setUser(currentUser);
          return;
        } catch (tokenError) {
          console.error('Failed to parse E2E token:', tokenError);
          throw new Error('Failed to parse E2E token');
        }
      }

      // MSAL mode: get user from MSAL active account
      const account: AccountInfo | null = await getActiveAccount();

      if (!account) {
        throw new Error('No active user account found');
      }

      // Extract roles from ID token claims
      const roles = (account.idTokenClaims?.roles as string[]) || [];

      // Determine user role
      let userRole: UserRole;
      if (roles.length === 0) {
        // No roles assigned in Enterprise App
        userRole = null;
      } else if (roles.includes('Admin')) {
        userRole = 'Admin';
      } else if (roles.includes('PNOps')) {
        userRole = 'PNOps';
      } else if (roles.includes('ViewOnly')) {
        userRole = 'ViewOnly';
      } else {
        // Has roles but none we recognize - default to ViewOnly
        userRole = 'ViewOnly';
      }

      // Extract user information from account
      const currentUser: CurrentUser = {
        // Use OID from token claims (same field backend uses for Azure AD Object ID)
        id:
          (account.idTokenClaims?.oid as string) ||
          account.localAccountId ||
          account.homeAccountId ||
          account.username,
        email: account.username || account.name || '',
        name: account.name || account.username || 'Unknown User',
        role: userRole,
      };

      setUser(currentUser);
    } catch (err) {
      console.error('Error getting current user:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to get current user',
      );
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    getCurrentUser();
  }, [getCurrentUser]);

  return { user, isLoading, error, refetch: getCurrentUser };
}
